import React, { useState, useMemo } from 'react';
import { getFirestore, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { MdDelete, MdFileDownload } from 'react-icons/md';
import { Helmet } from 'react-helmet';

const JobApplicationsTable = ({ applications }) => {
  const firestore = getFirestore();
  const [appList, setAppList] = useState(applications);

  // State for filters
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [jobIdFilter, setJobIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Handle status update
  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      const applicationRef = doc(firestore, 'applications', applicationId);
      await updateDoc(applicationRef, { status: newStatus });

      // Update local state to reflect the change
      setAppList((prevApps) =>
        prevApps.map((app) =>
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );

      toast.success(`Application status updated to ${newStatus}!`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update application status. Please try again.');
    }
  };

  // Handle status filter change
  const handleChange = (e, applicationId) => {
    const newStatus = e.target.value;
    handleStatusUpdate(applicationId, newStatus);
  };

  // Handle delete application
  const handleDelete = async (applicationId) => {
    try {
      const applicationRef = doc(firestore, 'applications', applicationId);
      await deleteDoc(applicationRef);

      // Update local state to remove the deleted application
      setAppList((prevApps) => prevApps.filter((app) => app.id !== applicationId));

      toast.success('Application deleted successfully!');
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Failed to delete application. Please try again.');
    }
  };

  // Filter applications based on the filter criteria
  const filteredApplications = useMemo(() => {
    return appList.filter((application) => {
      const matchesName = application.name.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesEmail = application.email.toLowerCase().includes(emailFilter.toLowerCase());
      const matchesMobile = application.mobile.toLowerCase().includes(mobileFilter.toLowerCase());
      const matchesJobId = application.jobId.toLowerCase().includes(jobIdFilter.toLowerCase());
      const matchesStatus = statusFilter ? application.status === statusFilter : true;

      return matchesName && matchesEmail && matchesMobile && matchesJobId && matchesStatus;
    });
  }, [nameFilter, emailFilter, mobileFilter, jobIdFilter, statusFilter, appList]);

  return (
    <>
    <Helmet>
      <title>Job Applications - Nexify Infosystems</title>
      <meta name="description" content="Manage job applications, review candidate details, and update application status for Nexify careers." />
      <meta name="keywords" content="job applications, job management, recruitment, candidate review, Nexify careers, application status, delete application" />
    </Helmet>
    <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
        <input
          type="text"
          placeholder="Filter by Name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full"
        />
        <input
          type="text"
          placeholder="Filter by Email"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full"
        />
        <input
          type="text"
          placeholder="Filter by Mobile"
          value={mobileFilter}
          onChange={(e) => setMobileFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full"
        />
        <input
          type="text"
          placeholder="Filter by Job ID"
          value={jobIdFilter}
          onChange={(e) => setJobIdFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full"
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Accepted">Accepted</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

    <div className="overflow-x-auto bg-white shadow-md rounded-lg">
      {/* Filters */}
     
      {/* Table */}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Applied At
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Mobile
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Job ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Resume
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredApplications.length > 0 ? (
            filteredApplications.map((application) => (
              <tr key={application.id} className="hover:bg-gray-100 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {new Date(application.appliedAt.seconds * 1000).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{application.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{application.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{application.mobile}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{application.jobId}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <a
                    href={application.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-2 py-1 border border-blue-600 rounded-md text-lg text-blue-600"
                  >
                    <MdFileDownload />
                  </a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select
                    value={application.status || 'Pending'}
                    onChange={(e) => handleChange(e, application.id)}
                    className={`border rounded px-2 py-1 ${
                      application.status === 'Accepted'
                        ? 'text-green-600'
                        : application.status === 'Rejected'
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}
                  >
                    <option value="Pending" className="text-yellow-600">Pending</option>
                    <option value="Accepted" className="text-green-600">Accepted</option>
                    <option value="Rejected" className="text-red-600">Rejected</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleDelete(application.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <MdDelete className="text-lg" />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                No applications found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default JobApplicationsTable;
